import React, { useState } from "react";
import { ArrowRight, Check, Copy, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { joinWaitlist } from "@/lib/api";

export default function WaitlistCTA() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [copied, setCopied] = useState(false);

  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (!isValid) return toast.error("Please enter a valid email.");
    setLoading(true);
    try {
      const res = await joinWaitlist(email.trim());
      setSuccess(res);
      toast.success("You're in. Welcome to the community.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const refLink = success
    ? `${window.location.origin}/?ref=${success.referral_code}`
    : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(refLink);
      setCopied(true);
      toast.success("Referral link copied.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy. Long-press to copy manually.");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "DataSniper — early access",
          text: "Discover what your market is searching for. Join me on the DataSniper waitlist:",
          url: refLink,
        });
      } catch (error) {
        // User-cancelled share dialogs throw AbortError — ignore those; fall back to copy otherwise.
        if (error?.name !== "AbortError") {
          console.error("Share failed:", error);
          copyLink();
        }
      }
    } else {
      copyLink();
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      className="relative py-28 md:py-36 border-t border-white/5 overflow-hidden"
    >
      <div aria-hidden className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] ds-glow-radial pointer-events-none" />
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center relative">
        <p className="text-[11px] tracking-[0.32em] uppercase text-[#FF5000] mb-6">· Free · Join the community</p>
        <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold tracking-[-0.03em] text-white leading-[1.02]">
          Join the DataSniper community.
        </h2>
        <p className="mt-6 text-lg md:text-xl text-neutral-400 max-w-2xl mx-auto leading-relaxed">
          DataSniper is free — no account needed. Drop your email for product updates and new data sources,
          then hop into our Discord to shape the roadmap and talk markets with us.
        </p>

        <form data-testid="waitlist-form" onSubmit={submit} className="mt-12 max-w-xl mx-auto">
          {!success ? (
            <div className="flex flex-col sm:flex-row gap-px bg-neutral-900 border border-neutral-800 focus-within:border-[#FF5000] transition-colors">
              <input
                data-testid="waitlist-email-input"
                type="email" required placeholder="you@company.com"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-[#070707] text-white text-[15px] px-5 py-4 outline-none placeholder:text-neutral-600"
              />
              <button
                data-testid="waitlist-submit-btn" data-cta="true" type="submit"
                disabled={loading || !isValid}
                className="inline-flex items-center justify-center gap-2 bg-[#FF5000] hover:bg-[#FF7333] disabled:bg-neutral-800 disabled:text-neutral-500 text-white text-[14px] font-medium px-6 py-4 transition-colors"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Join the community<ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          ) : (
            <div data-testid="waitlist-success" className="ds-crosshair relative border border-[#FF5000]/40 bg-[#0a0a0a] p-8 text-left">
              <span className="ds-ch-bl" /><span className="ds-ch-br" />
              <div className="flex items-start gap-4 mb-6">
                <span className="inline-flex w-9 h-9 items-center justify-center border border-[#FF5000] flex-shrink-0">
                  <Check className="w-4 h-4 text-[#FF5000]" />
                </span>
                <div className="flex-1">
                  <p className="text-[11px] uppercase tracking-[0.28em] text-[#FF5000] mb-2">You're in</p>
                  <p className="text-white text-xl tracking-tight">Welcome to the community — you're #{success.position}.</p>
                </div>
              </div>

              <a
                href="https://discord.gg/nfCGCXyUY"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="success-discord-btn"
                className="flex items-center justify-center gap-2.5 w-full bg-[#5865F2] hover:bg-[#4752c4] text-white text-[14px] font-medium px-5 py-3.5 mb-6 transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden>
                  <path d="M20.317 4.3698a19.7913 19.7913 0 0 0-4.8851-1.5152.0741.0741 0 0 0-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 0 0-.0785-.037 19.7363 19.7363 0 0 0-4.8852 1.515.0699.0699 0 0 0-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 0 0 .0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 0 0 .0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 0 0-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 0 1-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 0 1 .0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 0 1 .0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 0 1-.0066.1276 12.2986 12.2986 0 0 1-1.873.8914.0766.0766 0 0 0-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 0 0 .0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 0 0 .0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 0 0-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
                </svg>
                Join our Discord
              </a>

              <div className="border-t border-neutral-900 pt-6">
                <p className="text-[11px] uppercase tracking-[0.28em] text-neutral-500 mb-3">Move up the list</p>
                <p className="text-neutral-300 text-[14px] leading-relaxed mb-4">
                  Share your link. Each friend who joins bumps you up{" "}
                  <span className="text-[#FF5000] font-medium">{success.bump_per_referral} spots</span>.
                </p>

                <div className="flex flex-col sm:flex-row gap-px bg-neutral-900 border border-neutral-800 mb-3">
                  <input
                    data-testid="referral-link"
                    readOnly value={refLink}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 bg-[#070707] text-neutral-300 text-[13px] px-4 py-3 outline-none ds-mono"
                  />
                  <button
                    data-testid="copy-ref-btn" type="button" onClick={copyLink}
                    className="inline-flex items-center justify-center gap-2 text-white text-[13px] font-medium px-4 py-3 border-l border-neutral-800 hover:bg-neutral-900 transition-colors"
                  >
                    {copied ? <Check className="w-4 h-4 text-[#FF5000]" /> : <Copy className="w-4 h-4" />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                  <button
                    data-testid="share-ref-btn" type="button" onClick={share}
                    className="inline-flex items-center justify-center gap-2 bg-[#FF5000] hover:bg-[#FF7333] text-white text-[13px] font-medium px-4 py-3 transition-colors"
                  >
                    <Share2 className="w-4 h-4" /> Share
                  </button>
                </div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-neutral-600">
                  Your code · <span className="ds-mono text-neutral-300">{success.referral_code}</span> · {success.referred_count} referrals so far
                </p>
              </div>
            </div>
          )}
          <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-600 mt-4">No spam · Unsubscribe any time</p>
        </form>
      </div>
    </section>
  );
}
